import { DEVICE_TYPES, GRAPH_TYPES } from './protocol';

const HEATER_GRAPHS = Object.freeze([
  GRAPH_TYPES.HEATER,
  GRAPH_TYPES.ENCLOSURE,
  GRAPH_TYPES.OUTSIDE,
  GRAPH_TYPES.SNOW,
  GRAPH_TYPES.WIND,
]);

const GAS_GRAPHS = Object.freeze([
  GRAPH_TYPES.GAS,
  GRAPH_TYPES.GAS_ENCLOSURE,
  GRAPH_TYPES.OUTSIDE,
  GRAPH_TYPES.GAS_SNOW,
  GRAPH_TYPES.GAS_WIND,
]);

// The programs a device offers, in the order its control box shows them.
// Each name is the device-state key (`flat[location][machine][program]`).
const ELECTRIC_PROGRAMS = Object.freeze([
  'instantHeat',
  'snowSensor',
  'optionalConstantTemp',
  'heatingSchedule',
  'windFactor',
]);

const GAS_PROGRAMS = Object.freeze([
  'instantHeat',
  'fanOnly',
  'snowSensor',
  'heatingSchedule',
  'windFactor',
]);

// What makes ESS, TGS and TES different, in one place. Keyed by the lowercase
// name components pass as `swtName`.
export const SYSTEMS = Object.freeze({
  ess: Object.freeze({
    key: 'ess',
    deviceType: DEVICE_TYPES.ESS,
    resource: 'switches',
    freezeByField: 'freeze_by',
    zonesQuery: undefined,
    isGas: false,
    graphTypes: HEATER_GRAPHS,
    color: '#83FFFF',
    programs: ELECTRIC_PROGRAMS,
    hasSSRs: true,
    conflict: null,
    sibling: null,
  }),
  tgs: Object.freeze({
    key: 'tgs',
    deviceType: DEVICE_TYPES.TGS,
    resource: 'blowers',
    freezeByField: 'freeze_by',
    zonesQuery: undefined,
    isGas: true,
    graphTypes: GAS_GRAPHS,
    color: '#FF7800',
    programs: GAS_PROGRAMS,
    hasSSRs: false,
    // TGS and TES run on the same blower: starting one while the device's
    // `current_run` is the other asks the operator first. The labels are what
    // the conflict message shows.
    conflict: Object.freeze({
      otherRun: 'electrical',
      currentSwitch: 'tes-typhoon electric system',
      desiredSwitch: 'tgs-typhoon gas system',
      systemTarget: 'gas',
    }),
    // The other system on the same blower: the card's header shows its name,
    // lit while the device reports it enabled (`device[activeField]`).
    sibling: Object.freeze({ label: 'tes', activeField: 'isTESActive' }),
  }),
  tes: Object.freeze({
    key: 'tes',
    deviceType: DEVICE_TYPES.TES,
    resource: 'blowers',
    freezeByField: 'e_freeze_by',
    zonesQuery: Object.freeze({ type: DEVICE_TYPES.TES }),
    isGas: false,
    graphTypes: HEATER_GRAPHS,
    color: '#95FF45',
    programs: ELECTRIC_PROGRAMS,
    hasSSRs: true,
    conflict: Object.freeze({
      otherRun: 'gas',
      currentSwitch: 'tgs-typhoon gas system',
      desiredSwitch: 'tgs-typhoon electric system',
      systemTarget: 'electric',
    }),
    sibling: Object.freeze({ label: 'tgs', activeField: 'isTGSActive' }),
  }),
});

export const SYSTEM_KEYS = Object.keys(SYSTEMS);

// Accepts 'ess' or DEVICE_TYPES.ESS: both are in use as the system argument.
export const systemOf = (name) => {
  const key = String(name).toLowerCase();
  if (!SYSTEM_KEYS.includes(key)) throw new Error(`unknown system: ${name}`);
  return SYSTEMS[key];
};
